import * as React from "react";

import { useEffect, useRef } from "react";

import Button from "../components/shared/button";
import ButtonElement from "../components/shared/button-element";
import Footer from "../components/sections/footer";
import Grid from "../components/shared/grid";
import Layout from "../components/shared/layout";
import { Link } from "gatsby";
import Navbar from "../components/navbar";
import { SecondaryPageTitle } from "../components/shared/secondary-page-elements";
import Section from "../components/shared/section";
import Seo from "../components/shared/seo";
import { StaticImage } from "gatsby-plugin-image";
import { isMobile } from "react-device-detect";
import { motion } from "framer-motion";
import styled from "styled-components";
import useSmartScriptLink from "../components/shared/hooks/use-smart-script-link";

const DownloadPage = () => {
  const iosLink = useSmartScriptLink();
  const qrDivId = "af-qr-code";
  const qrRendered = useRef(false);

  useEffect(() => {
    const addQr = () => {
      if (!window.AF_SMART_SCRIPT) {
        return false;
      }
      if (
        !isMobile &&
        window.AF_SMART_SCRIPT &&
        window.AF_SMART_SCRIPT.displayQrCode &&
        !qrRendered.current
      ) {
        const qrDiv = document.getElementById(qrDivId);
        if (qrDiv) {
          window.AF_SMART_SCRIPT.displayQrCode(qrDivId, {
            codeColor: "#3A4250",
          });
          qrRendered.current = true;
        }
      }
      return true;
    };
    if (!addQr()) {
      const id = setInterval(() => {
        if (addQr()) {
          clearInterval(id);
        }
      }, 500);
      return () => clearInterval(id);
    }
  }, [isMobile]);

  const QrVariants = {
    small: {
      backgroundColor: "rgba(1,9,18,1)",
      width: "64px",
      height: "64px",
      originY: 1,
      originX: 1,
    },
    big: {
      backgroundColor: "rgba(5,20,38,1)",
      width: "10rem",
      height: "10rem",
      originY: 1,
      originX: 1,
    },
  };

  const pathVariants = {
    small: {
      fill: "#5D7696",
    },
    big: {
      fill: "#D2DAE4",
    },
  };
  const labelVariants = {
    small: {
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.3,
        duration: 0.75,
        type: "spring",
        bounce: 0,
      },
    },
    big: {
      x: 5,
      opacity: 0,
      transition: {
        duration: 0,
      },
    },
  };

  return (
    <Layout>
      <Seo
        title={"Download | Copilot"}
        ogTitle={"Copilot – Download our best-in-class Mac and iPhone apps"}
      />
      <Navbar />
      <Section>
        <DownloadTitle>Let's get started</DownloadTitle>
        <Grid>
          <Container>
            {isMobile ? (
            <Part>
              <Title>
                <Icon>
                  <IPhoneSVG />
                </Icon>
                <Label>
                  <Text>For iPhone & iPad</Text>
                  <MutedText>Requires iOS/iPadOS 15 or above</MutedText>
                </Label>
              </Title>
              <MobilePlatformOptions>
                <Button href={iosLink} id="ios_link" />
                <Scannable
                  initial="small"
                  transition={{ type: "spring", bounce: 0, duration: 0.3 }}
                >
                  <QrContainer variants={QrVariants}>
                    <div
                      id={qrDivId}
                      style={{
                        width: '64px',
                        height: '64px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        zIndex: 2,
                      }}
                    />
                  </QrContainer>
                </Scannable>
              </MobilePlatformOptions>
            </Part>
            ) : ''}
            <Part>
              <Title>
                <Icon>
                  <MacSVG />
                </Icon>
                <Label>
                  <Text>For Mac</Text>
                  <MutedText>Requires macOS 12 (Monterey) or above</MutedText>
                </Label>
              </Title>
              <PlatformOptions style={{ marginTop: isMobile ? 24 : 48 }}>
                {isMobile ? (
                  <p style={{ paddingRight: 32, maxWidth: 330 }}>
                    Please visit this page in a desktop browser to download the
                    Mac app.
                  </p>
                ) : (
                  <>
                    <ButtonElement
                      outbound
                      href="https://releases.copilot.money/latest"
                      style={{ backgroundColor: "white" }}
                    >
                      Download for Mac
                    </ButtonElement>
                    <p>Apple Silicon and Intel Compatible</p>
                  </>
                )}
              </PlatformOptions>
            </Part>
            {!isMobile ? (
            <Part>
              <Title>
                <Icon>
                  <IPhoneSVG />
                </Icon>
                <Label>
                  <Text>For iPhone & iPad</Text>
                  <MutedText>Requires iOS/iPadOS 15 or above</MutedText>
                </Label>
              </Title>
              <MobilePlatformOptions>
                <Button href={iosLink} id="ios_link" />
                <Scannable
                  initial="small"
                  transition={{ type: "spring", bounce: 0, duration: 0.3 }}
                >
                  <QrContainer variants={QrVariants}>
                    <div
                      id={qrDivId}
                      style={{
                        width: '64px',
                        height: '64px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        zIndex: 2,
                      }}
                    />
                  </QrContainer>
                </Scannable>
              </MobilePlatformOptions>
            </Part>
            ) : ''}
          </Container>
        </Grid>
        <Meta>
          <StaticImage
            src="../components/sections/download/usa-flag.png"
            alt="Copilot is available in the US"
            width={40}
            quality={100}
            placeholder="none"
          />
          <p>Copilot is only available in the United States</p>
          <p className="muted">
            Submit your vote for the country you'd like to see Copilot launch in
            next:
          </p>
          <LeaderboardLink to="/country-leaderboard">
            <LinkContent>Country Leaderboard</LinkContent>
          </LeaderboardLink>
        </Meta>
      </Section>
      <Footer isDownloadPage />
    </Layout>
  );
};

const Label = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-items: baseline;
  ${(props) => props.theme.device.laptopLg} {
    flex-direction: row;
    gap: 24px;
  }
`;

const LinkContent = styled.div`
  color: ${(props) => props.theme.color.textLink};
  display: inline-block;
  position: relative;
  margin: 0.5rem auto;
  padding-bottom: 0.1em;
  text-align: center;
  font-weight: 600;
  font-size: ${(props) => props.theme.fontSize.regular};
  overflow: hidden;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 0.1em;
    background-color: ${(props) => props.theme.color.textLink};
    opacity: 1;
    transform: translate3d(-101%, 0, 0);
    transition: opacity 300ms, transform 300ms;
  }
  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 0.1em;
    background-color: ${(props) => props.theme.color.textLink};
    opacity: 0.3;
    transform: translate3d(0, 0, 0);
  }
  & svg {
    color: ${(props) => props.theme.color.textLink};
    width: 1.5em;
    margin: 0.5em auto;
    display: block;
    opacity: 0.3;
    transition: color 300ms, opacity 300ms;
  }
`;

const LeaderboardLink = styled(Link)`
  grid-column: col-start / span 12;
  place-self: center;
  max-width: 300px;
  text-align: center;
  margin-top: 1rem;
  &:hover,
  &:focus {
    & ${LinkContent} {
      &::after {
        transform: translate3d(0, 0, 0);
      }
      & svg {
        opacity: 1;
        color: ${(props) => props.theme.color.textLink};
      }
    }
  }
`;

const Meta = styled.div`
  margin: 0 auto;
  text-align: center;
  padding-top: 4rem;
  & .gatsby-image-wrapper {
    margin-bottom: 16px;
  }
  & p {
    max-width: 335px;
    margin: 0 auto;
    font-weight: 600;
    font-size: ${(props) => props.theme.fontSize.regular};
    line-height: 20px;
    color: ${(props) => props.theme.color.textPrimary};
  }
  & p.muted {
    padding-top: 8px;
    color: ${(props) => props.theme.color.textSecondary};
  }
`;

const MacSVG = () => {
  return (
    <svg
      width="35"
      height="29"
      viewBox="0 0 35 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.30664 23.7129H13.0293V26.1875H12.9746C12.291 26.1875 11.7305 26.748 11.7305 27.4316C11.7305 28.1016 12.291 28.6621 12.9746 28.6621H22.0117C22.6953 28.6621 23.2559 28.1016 23.2559 27.4316C23.2559 26.748 22.6953 26.1875 22.0117 26.1875H21.957V23.7129H30.666C32.7988 23.7129 34.084 22.4414 34.084 20.3086V3.83398C34.084 1.70117 32.7988 0.429688 30.666 0.429688H4.30664C2.1875 0.429688 0.902344 1.70117 0.902344 3.83398V20.3086C0.902344 22.4414 2.1875 23.7129 4.30664 23.7129ZM4.10156 17.3965C3.70508 17.3965 3.52734 17.2461 3.52734 16.8223V3.98438C3.52734 3.41016 3.88281 3.04102 4.4707 3.04102H30.5156C31.1035 3.04102 31.459 3.41016 31.459 3.98438V16.8223C31.459 17.2461 31.2812 17.3965 30.8848 17.3965H4.10156Z"
        fill="white"
      />
    </svg>
  );
};

const IPhoneSVG = () => {
  return (
    <svg
      width="19"
      height="31"
      viewBox="0 0 19 31"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.1543 30.0723H14.9004C17.2246 30.0723 18.7695 28.5547 18.7695 26.2988V3.93164C18.7695 1.67578 17.2246 0.158203 14.9004 0.158203H4.1543C1.80273 0.158203 0.216797 1.67578 0.216797 3.93164V26.2988C0.216797 28.5547 1.80273 30.0723 4.1543 30.0723ZM4.60547 27.3242C3.52539 27.3242 2.89648 26.7227 2.89648 25.6836V4.54688C2.89648 3.50781 3.52539 2.91992 4.60547 2.91992H6.41016C6.62891 2.91992 6.73828 3.0293 6.73828 3.24805V3.60352C6.73828 4.19141 7.12109 4.58789 7.72266 4.58789H11.291C11.8789 4.58789 12.2754 4.19141 12.2754 3.60352V3.24805C12.2754 3.0293 12.3848 2.91992 12.6035 2.91992H14.3945C15.4746 2.91992 16.1035 3.50781 16.1035 4.54688V25.6836C16.1035 26.7227 15.4746 27.3242 14.3945 27.3242H4.60547ZM6.56055 26.2441H12.4531C12.8496 26.2441 13.1367 25.9707 13.1367 25.5605C13.1367 25.1367 12.8496 24.8633 12.4531 24.8633H6.56055C6.16406 24.8633 5.86328 25.1367 5.86328 25.5605C5.86328 25.9707 6.16406 26.2441 6.56055 26.2441Z"
        fill="currentColor"
      />
    </svg>
  );
};

const IPadSVG = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29.76 39.639">
      <path
        d="M9.703 35.438h9.967c.492 0 .826-.335.826-.827 0-.492-.334-.808-.826-.808H9.703c-.474 0-.808.316-.808.808 0 .492.334.827.808.827ZM0 34.962c0 2.777 1.951 4.64 4.87 4.64h19.634c2.918 0 4.869-1.863 4.869-4.64V4.658c0-2.777-1.951-4.658-4.87-4.658H4.87C1.951 0 0 1.88 0 4.658Zm2.83-.475V5.133c0-1.442.879-2.303 2.39-2.303h18.932c1.494 0 2.391.861 2.391 2.303v29.355c0 1.442-.896 2.285-2.39 2.285H5.22c-1.512 0-2.39-.843-2.39-2.285Z"
        fill="currentColor"
      />
    </svg>
  );
};

const DownloadTitle = styled(SecondaryPageTitle)`
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  background: linear-gradient(90deg, #8ebdf9 3.07%, #d2dae4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  grid-column: span 12;
`;

const Icon = styled.div`
  width: 40px;
  height: 30px;
  display: flex;
  justify-content: center;
  & svg path {
    fill: ${(props) => props.theme.color.uiPillGround};
  }
`;
const Title = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;
const PlatformOptions = styled.div`
  margin-left: 64px;
  margin-top: 24px;
  margin-bottom: 24px;
  display: flex;
  // align-items: baseline;
  align-items: center;
  gap: 24px;
  & p {
    color: ${(props) => props.theme.color.textPrimary};
    font-weight: 500;
  }
`;
const MobilePlatformOptions = styled(PlatformOptions)`
  justify-content: space-between;
  margin-right: 40px;
  position: relative;
`;
const QrContainer = styled(motion.div)`
  cursor: none;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(1,9,18,1);
  border-radius: 0px;
  overflow: hidden;
  & > #af-qr-code canvas {
    width: 64px !important;
    height: 64px !important;
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
`;
const VectorQr = styled(motion.svg)`
  width: 100%;
  height: 100%;
`;
const Scannable = styled(motion.div)`
  display: none;
  ${(props) => props.theme.device.laptop} {
    display: flex;
  }
  align-items: center;
  gap: 16px;
  right: 40px;
  bottom: 40px;
  originx: 1;
  originy: 1;
  ${(props) => props.theme.device.laptopLg} {
    right: 53px;
    bottom: 53px;
  }
`;
const QrLabel = styled(motion.div)`
  color: ${(props) => props.theme.color.textSecondary};
  font-weight: 600;
  user-select: none;
`;
const Text = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  vertical-align: bottom;
`;
const MutedText = styled(Text)`
  font-size: 0.8rem;
  color: ${(props) => props.theme.color.textSecondary};
  font-weight: 500;
  vertical-align: bottom;
  margin-bottom: -3px;
  ${(props) => props.theme.device.tablet} {
    font-size: 1rem;
  }
`;
const Part = styled.div`
  padding: 20px;
  border: 1px solid rgba(48, 137, 255, 0.2);
  border-radius: 10px;
`;
const Container = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: 200px 200px;
  border-radius: 10px;
  background: radial-gradient(
      94.91% 94.91% at 50% 50%,
      rgba(1, 69, 137, 0.42) 0%,
      rgba(0, 0, 0, 0) 100%
    ),
    rgba(5, 24, 48, 0.2);
  // box-shadow: 0px -40px 116px -40px rgba(38, 91, 181, 0.1), 0px 4px 116px -110px rgba(106, 167, 248, 0.8);
  // filter: drop-shadow(0px -40px 116px -40px rgba(38, 91, 181, 0.1)), drop-shadow(0px 4px 116px -110px rgba(106, 167, 248, 0.8));
  grid-column: span 12;
  margin-top: 40px;
  ${(props) => props.theme.device.laptop} {
    grid-column: col-start 4 / span 6;
  }
  overflow: hidden;
  & ${Part}:first-child {
    border-bottom: 3px solid #020c19;
    border-radius: 10px 10px 0 0;
  }
  & ${Part}:nth-child(2) {
    border-top: none;
    border-radius: 0 0 10px 10px;
  }
`;

export default DownloadPage;
